import React from 'react'

import { ArrowLeft, Plus, Trash2 } from 'lucide-react'
import { Controller, UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { AlegraLocationOption } from '@/core/hooks/payment/useAlegraLocations'
import { Button } from '@/shared/components/atoms/Button'
import { Input } from '@/shared/components/atoms/Input'
import { Label } from '@/shared/components/atoms/Label/Label'
import { PhoneInput } from '@/shared/components/atoms/PhoneNumber'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/shared/components/atoms/Select'
import { CountrySelector } from '@/shared/components/molecules/CountrySelector/CountrySelector'

import {
	AlegraChlAddressFields,
	AlegraArgAddressFields,
	AlegraEspAddressFields,
	AlegraColAddressFields,
	DefaultAddressFields,
} from './address'
import { AlegraArgFields } from './alegra/AlegraArgFields'
import { AlegraColFields } from './alegra/AlegraColFields'
import { AlegraEspFields } from './alegra/AlegraEspFields'
import { AlegraMexFields } from './alegra/AlegraMexFields'
import { BillingInformationFormData } from './BillingInformation.types'

interface Language {
	id: number
	name: string
	ietf: string
	active: boolean
}

export interface BillingInformationUIProps {
	form: UseFormReturn<BillingInformationFormData>
	onBack: () => void
	onSubmit: (data: BillingInformationFormData) => void
	isSaving: boolean
	languages: Language[]
	// Validation flags
	hasMxValidations: boolean
	hasArValidations: boolean
	hasClValidations: boolean
	hasEsValidations: boolean
	hasCoValidations: boolean
	// Location data
	chlRegions: AlegraLocationOption[]
	chlCommunes: AlegraLocationOption[]
	argProvinces: AlegraLocationOption[]
	argCities: AlegraLocationOption[]
	espProvinces: AlegraLocationOption[]
	espCities: AlegraLocationOption[]
	colDepartments: AlegraLocationOption[]
	colCities: AlegraLocationOption[]
	colZipCodes: AlegraLocationOption[]
	// Loading states
	isLoadingChlCommunes: boolean
	isLoadingArgCities: boolean
	isLoadingEspCities: boolean
	isLoadingColCities: boolean
	isLoadingColZipCodes: boolean
	// Selected values for location dependencies
	selectedChlRegion: string
	selectedArgProvince: string
	selectedEspProvinceId: number | undefined
	selectedColDepartmentId: number | undefined
	selectedColCity: string
	// Colombia kind of person state
	kindOfPerson: string | undefined
	setKindOfPerson: (value: string | undefined) => void
	// Edit mode (will disable certain fields)
	isEditMode?: boolean
	// Hide header (when embedded in another page like subscribe flow)
	hideHeader?: boolean
	// Form ID for external submission
	formId?: string
	// Hide action buttons (save/cancel)
	hideActionButtons?: boolean
}

export const BillingInformationUI: React.FC<BillingInformationUIProps> = ({
	form,
	onBack,
	onSubmit,
	isSaving,
	languages,
	hasMxValidations,
	hasArValidations,
	hasClValidations,
	hasEsValidations,
	hasCoValidations,
	chlRegions,
	chlCommunes,
	argProvinces,
	argCities,
	espProvinces,
	espCities,
	colDepartments,
	colCities,
	colZipCodes,
	isLoadingChlCommunes,
	isLoadingArgCities,
	isLoadingEspCities,
	isLoadingColCities,
	isLoadingColZipCodes,
	selectedChlRegion,
	selectedArgProvince,
	selectedEspProvinceId,
	selectedColDepartmentId,
	selectedColCity,
	kindOfPerson,
	setKindOfPerson,
	isEditMode = false,
	hideHeader = false,
	formId,
	hideActionButtons = false,
}) => {
	const { t } = useTranslation('common')

	// Address fields based on country
	const addressFields = hasClValidations ? (
		<AlegraChlAddressFields
			regions={chlRegions}
			communes={chlCommunes}
			isLoadingCommunes={isLoadingChlCommunes}
			selectedRegion={selectedChlRegion}
		/>
	) : hasArValidations ? (
		<AlegraArgAddressFields
			provinces={argProvinces}
			cities={argCities}
			isLoadingCities={isLoadingArgCities}
			selectedProvince={selectedArgProvince}
		/>
	) : hasEsValidations ? (
		<AlegraEspAddressFields
			provinces={espProvinces}
			cities={espCities}
			isLoadingCities={isLoadingEspCities}
			selectedProvinceId={selectedEspProvinceId}
		/>
	) : hasCoValidations ? (
		<AlegraColAddressFields
			departments={colDepartments}
			cities={colCities}
			zipCodes={colZipCodes}
			isLoadingCities={isLoadingColCities}
			isLoadingZipCodes={isLoadingColZipCodes}
			selectedDepartmentId={selectedColDepartmentId}
			selectedCity={selectedColCity}
		/>
	) : (
		<DefaultAddressFields />
	)

	return (
		<div className="w-full">
			{/* Header */}
			{!hideHeader && (
				<div className="flex items-center gap-4 mb-8">
					<Button
						variant="ghost"
						size="icon"
						onClick={onBack}
						className="h-8 w-8 cursor-pointer"
					>
						<ArrowLeft className="h-4 w-4" />
					</Button>
					<h1 className="text-2xl font-bold text-foreground">
						{t('account.billing_information.title')}
					</h1>
				</div>
			)}

			{/* Form */}
			<form
				id={formId}
				onSubmit={form.handleSubmit(onSubmit)}
				className="space-y-6"
			>
				<div className="space-y-6">
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
						{/* Official Company Name - Full Width */}
						<div className="flex flex-col gap-2 md:col-span-2">
							<Label htmlFor="official_company_name">
								{t('account.billing_information.official_company_name')} *
							</Label>
							<Input
								id="official_company_name"
								{...form.register('official_company_name')}
								className="bg-input"
								aria-invalid={!!form.formState.errors.official_company_name}
							/>
							{form.formState.errors.official_company_name && (
								<p className="text-sm text-destructive">
									{form.formState.errors.official_company_name.message}
								</p>
							)}
						</div>

						{/* Company Tax ID & Billing Contact Full Name - Side by Side */}
						<div className="flex flex-col gap-2">
							<Label htmlFor="tax">
								{t('account.billing_information.company_tax_id')} *
							</Label>
							<Input
								id="tax"
								{...form.register('tax')}
								className="bg-input"
								disabled={isEditMode}
								aria-invalid={!!form.formState.errors.tax}
							/>
							{form.formState.errors.tax && (
								<p className="text-sm text-destructive">
									{form.formState.errors.tax.message}
								</p>
							)}
						</div>
						<div className="flex flex-col gap-2">
							<Label htmlFor="billing_contact_full_name">
								{t('account.billing_information.billing_contact_full_name')} *
							</Label>
							<Input
								id="billing_contact_full_name"
								{...form.register('billing_contact_full_name')}
								className="bg-input"
								aria-invalid={!!form.formState.errors.billing_contact_full_name}
							/>
							{form.formState.errors.billing_contact_full_name && (
								<p className="text-sm text-destructive">
									{form.formState.errors.billing_contact_full_name.message}
								</p>
							)}
						</div>

						{/* Notification Emails - Full Width */}
						<div className="flex flex-col gap-2 md:col-span-2">
							<Label>
								{t('account.billing_information.notification_emails')} *
							</Label>
							<div className="space-y-2">
								{(form.watch('notification_emails') || ['']).map(
									(_: string, index: number) => (
										<div key={index} className="flex gap-2">
											<Controller
												name={
													`notification_emails.${index}` as `notification_emails.${number}`
												}
												control={form.control}
												render={({ field, fieldState: { error } }) => (
													<div className="flex-1">
														<Input
															{...field}
															type="email"
															placeholder={t(
																'account.billing_information.notification_email_placeholder'
															)}
															className="bg-input h-10"
															aria-invalid={!!error}
														/>
														{error && (
															<p className="text-sm text-destructive mt-1">
																{error.message}
															</p>
														)}
													</div>
												)}
											/>
											{(form.watch('notification_emails') || []).length > 1 && (
												<Button
													type="button"
													variant="outline"
													size="icon"
													onClick={() => {
														const currentEmails =
															form.getValues('notification_emails') || []
														const newEmails = currentEmails.filter(
															(_: string, i: number) => i !== index
														)
														form.setValue('notification_emails', newEmails)
													}}
													className="h-10 w-10 shrink-0"
												>
													<Trash2 className="h-4 w-4" />
												</Button>
											)}
										</div>
									)
								)}
								<Button
									type="button"
									variant="outline"
									onClick={() => {
										const currentEmails = form.getValues(
											'notification_emails'
										) || ['']
										form.setValue('notification_emails', [...currentEmails, ''])
									}}
									className="w-full md:w-auto"
								>
									<Plus className="h-4 w-4 mr-2" />
									{t('account.billing_information.add_email')}
								</Button>
							</div>
							{form.formState.errors.notification_emails && (
								<p className="text-sm text-destructive">
									{form.formState.errors.notification_emails.message}
								</p>
							)}
						</div>

						{/* Street - Full Width */}
						<div className="flex flex-col gap-2 md:col-span-2">
							<Label htmlFor="street">
								{t('account.billing_information.street')} *
							</Label>
							<Input
								id="street"
								{...form.register('street')}
								className="bg-input"
								aria-invalid={!!form.formState.errors.street}
							/>
							{form.formState.errors.street && (
								<p className="text-sm text-destructive">
									{form.formState.errors.street.message}
								</p>
							)}
						</div>

						{/* Address Fields */}
						{addressFields}

						{/* Country - Full Width */}
						<div className="flex flex-col gap-2 md:col-span-2">
							<Label htmlFor="country">
								{t('account.billing_information.country')} *
							</Label>
							<Controller
								name="country"
								control={form.control}
								render={({ field }) => (
									<CountrySelector
										value={field.value}
										onChange={field.onChange}
										placeholder={t('account.user_profile.select_country')}
										className={`bg-input ${
											form.formState.errors.country ? 'border-destructive' : ''
										}`}
									/>
								)}
							/>
							{form.formState.errors.country && (
								<p className="text-sm text-destructive">
									{form.formState.errors.country.message}
								</p>
							)}
						</div>

						{/* Invoice Language - Full Width */}
						<div className="flex flex-col gap-2 md:col-span-2">
							<Label htmlFor="invoice_language">
								{t('account.billing_information.invoice_language')}
							</Label>
							<Controller
								name="invoice_language"
								control={form.control}
								render={({ field }) => (
									<Select
										value={field.value || ''}
										onValueChange={field.onChange}
									>
										<SelectTrigger
											className="bg-input w-full"
											aria-invalid={!!form.formState.errors.invoice_language}
										>
											<SelectValue
												placeholder={t(
													'account.billing_information.select_invoice_language'
												)}
											/>
										</SelectTrigger>
										<SelectContent>
											{languages
												.filter((lang) => lang.active)
												.map((language) => (
													<SelectItem key={language.id} value={language.ietf}>
														{language.name}
													</SelectItem>
												))}
										</SelectContent>
									</Select>
								)}
							/>
							{form.formState.errors.invoice_language && (
								<p className="text-sm text-destructive">
									{form.formState.errors.invoice_language.message}
								</p>
							)}
						</div>

						{/* Contact Phone & Contact Name - Side by Side */}
						<div className="flex flex-col gap-2">
							<Label htmlFor="contact_phone">
								{t('account.billing_information.contact_phone')}
							</Label>
							<Controller
								name="contact_phone"
								control={form.control}
								render={({ field }) => (
									<PhoneInput
										value={field.value || ''}
										onChange={field.onChange}
										placeholder={t(
											'account.billing_information.contact_phone_placeholder'
										)}
										className="bg-input"
										aria-invalid={!!form.formState.errors.contact_phone}
									/>
								)}
							/>
							{form.formState.errors.contact_phone && (
								<p className="text-sm text-destructive">
									{form.formState.errors.contact_phone.message}
								</p>
							)}
						</div>
						<div className="flex flex-col gap-2">
							<Label htmlFor="contact_name">
								{t('account.billing_information.contact_name')}
							</Label>
							<Input
								id="contact_name"
								{...form.register('contact_name')}
								className="bg-input h-10"
								aria-invalid={!!form.formState.errors.contact_name}
							/>
							{form.formState.errors.contact_name && (
								<p className="text-sm text-destructive">
									{form.formState.errors.contact_name.message}
								</p>
							)}
						</div>

						{/* Alegra MEX Fields */}
						{hasMxValidations && <AlegraMexFields />}

						{/* Alegra ARG Fields */}
						{hasArValidations && <AlegraArgFields isEditMode={isEditMode} />}

						{/* Alegra ESP Fields */}
						{hasEsValidations && <AlegraEspFields isEditMode={isEditMode} />}

						{/* Alegra COL Fields */}
						{hasCoValidations && (
							<AlegraColFields
								setKindOfPerson={setKindOfPerson}
								kindOfPerson={kindOfPerson}
								isEditMode={isEditMode}
							/>
						)}
					</div>
				</div>

				{!hideActionButtons && (
					<div className="flex gap-4 pt-6">
						<Button
							type="button"
							variant="outline"
							onClick={onBack}
							className="rounded-full cursor-pointer"
						>
							{t('actions.cancel')}
						</Button>
						<Button
							type="submit"
							className="rounded-full cursor-pointer"
							disabled={isSaving}
						>
							{isSaving ? t('actions.saving') : t('actions.save')}
						</Button>
					</div>
				)}
			</form>
		</div>
	)
}

export default BillingInformationUI
